export interface TaxOptions {
  revenue: number
  expense: number
  salaryIncome?: number
  socialInsurance?: number
  isBusinessTaxable?: boolean
  withheldTax?: number
  businessWithheld?: number
  blueDeduction?: number
  mutualAidDeduction?: number
}

export interface TaxResult {
  revenue: number
  expense: number
  income: number
  salaryIncome: number
  salaryDeduction: number
  salaryTaxable: number
  socialInsurance: number
  withheldTax: number
  businessWithheld: number
  blueDeduction: number
  mutualAidDeduction: number
  basicDeduction: number
  taxableBase: number
  taxableIncome: number
  residentTaxable: number
  incomeTax: number
  reconstructionTax: number
  incomeTaxTotal: number
  additionalIncomeTax: number
  residentTax: number
  businessTax: number
  totalTax: number
  takeHome: number
}

export function calcSalaryDeduction(salary: number): number {
  if (salary <= 0) return 0
  if (salary <= 1_900_000) return Math.min(salary, 650_000)
  if (salary <= 3_600_000) return Math.floor(salary * 0.3 + 80_000)
  if (salary <= 6_600_000) return Math.floor(salary * 0.2 + 440_000)
  if (salary <= 8_500_000) return Math.floor(salary * 0.1 + 1_100_000)
  return 1_950_000
}

export function calcBasicDeduction(totalIncome: number): number {
  if (totalIncome <= 1_320_000) return 950_000
  // 88万/68万/63万の段階は令和7・8年分の特例（令和9年分以後は58万）。
  if (totalIncome <= 3_360_000) return 880_000
  if (totalIncome <= 4_890_000) return 680_000
  if (totalIncome <= 6_550_000) return 630_000
  if (totalIncome <= 23_500_000) return 580_000
  if (totalIncome <= 24_000_000) return 480_000
  if (totalIncome <= 24_500_000) return 320_000
  if (totalIncome <= 25_000_000) return 160_000
  return 0
}

export function marginalIncomeRate(taxable: number): number {
  if (taxable <= 1_950_000) return 0.05
  if (taxable <= 3_300_000) return 0.10
  if (taxable <= 6_950_000) return 0.20
  if (taxable <= 9_000_000) return 0.23
  if (taxable <= 18_000_000) return 0.33
  if (taxable <= 40_000_000) return 0.40
  return 0.45
}

export function annualContribution(
  monthly: number,
  startMonth = 1,
  annualMax = Infinity,
): number {
  const normalizedStartMonth = Math.min(12, Math.max(1, Math.trunc(startMonth)))
  return Math.min(
    Math.max(0, monthly) * (13 - normalizedStartMonth),
    Math.max(0, annualMax),
  )
}

export interface ReservePlan {
  monthlyTotal: number
  uncovered: number
  coveredCount: number
}

/**
 * Applies existing reserves to the earliest payments, then spreads each
 * remaining amount across the calendar months until its due date.
 */
export function calcReservePlan(
  items: { amount: number; due: string }[],
  reservedSoFar: number,
  now: Date,
): ReservePlan {
  let availableReserve = Math.max(0, reservedSoFar)
  let monthlyTotal = 0
  let uncovered = 0
  let coveredCount = 0

  const sortedItems = [...items].sort((a, b) => a.due.localeCompare(b.due))
  for (const item of sortedItems) {
    const amount = Math.max(0, item.amount)
    const appliedReserve = Math.min(availableReserve, amount)
    const remaining = amount - appliedReserve
    availableReserve -= appliedReserve

    if (amount > 0 && remaining === 0) {
      coveredCount += 1
      continue
    }

    const [dueYear, dueMonth] = item.due.split('-').map(Number)
    const monthsUntilDue = Math.max(
      1,
      (dueYear - now.getFullYear()) * 12 + (dueMonth - (now.getMonth() + 1)),
    )
    monthlyTotal += Math.ceil(remaining / monthsUntilDue)
    uncovered += remaining
  }

  return { monthlyTotal, uncovered, coveredCount }
}

export function calcTax({
  revenue,
  expense,
  salaryIncome = 0,
  socialInsurance = 0,
  isBusinessTaxable = false,
  withheldTax = 0,
  businessWithheld = 0,
  blueDeduction = 650_000,
  mutualAidDeduction = 0,
}: TaxOptions): TaxResult {
  const income = revenue - expense
  const businessTaxableBase = Math.max(0, income - blueDeduction)
  const salaryDeduction = calcSalaryDeduction(salaryIncome)
  const salaryTaxable = Math.max(0, salaryIncome - salaryDeduction)
  const taxableBase = businessTaxableBase + salaryTaxable
  const basicDeduction = calcBasicDeduction(taxableBase)
  const taxableIncome = Math.floor(Math.max(0, taxableBase - basicDeduction - socialInsurance - mutualAidDeduction) / 1_000) * 1_000
  const brackets: [number, number, number][] = [
    [1_950_000, 0.05, 0],
    [3_300_000, 0.10, 97_500],
    [6_950_000, 0.20, 427_500],
    [9_000_000, 0.23, 636_000],
    [18_000_000, 0.33, 1_536_000],
    [40_000_000, 0.40, 2_796_000],
    [Infinity, 0.45, 4_796_000],
  ]
  let incomeTax = 0
  for (const [limit, rate, deduction] of brackets) {
    if (taxableIncome <= limit) {
      incomeTax = Math.max(0, Math.floor(taxableIncome * rate - deduction))
      break
    }
  }
  const incomeTaxTotal = Math.floor(incomeTax * 1.021 / 100) * 100
  const reconstructionTax = incomeTaxTotal - incomeTax
  const additionalIncomeTax = Math.max(0, incomeTaxTotal - withheldTax - businessWithheld)
  const residentTaxable = Math.floor(Math.max(0, taxableBase - 430_000 - socialInsurance - mutualAidDeduction) / 1_000) * 1_000
  // 調整控除は資金繰り用の概算であるため省略する。
  const residentTax = Math.floor(residentTaxable * 0.1 / 100) * 100 + 5_000
  const businessTax = isBusinessTaxable && income > 2_900_000
    ? Math.floor((income - 2_900_000) * 0.05)
    : 0
  const totalTax = additionalIncomeTax + residentTax + businessTax
  const takeHome = revenue - expense - incomeTaxTotal - residentTax - businessTax - socialInsurance

  return {
    revenue,
    expense,
    income,
    salaryIncome,
    salaryDeduction,
    salaryTaxable,
    socialInsurance,
    withheldTax,
    businessWithheld,
    blueDeduction,
    mutualAidDeduction,
    basicDeduction,
    taxableBase,
    taxableIncome,
    residentTaxable,
    incomeTax,
    reconstructionTax,
    incomeTaxTotal,
    additionalIncomeTax,
    residentTax,
    businessTax,
    totalTax,
    takeHome,
  }
}
